/**
 * Locale-aware date and number formatting that reads from
 * `UserPreferences`. Pure functions so they can be called from anywhere
 * (server-data normalisation, table cells) — the React hook below adapts
 * them to the live preferences context.
 */
import { useMemo } from 'react'
import { usePreferences } from '@/src/modules/preferences/PreferencesProvider'
import type {
  DateFormat,
  NumberFormat,
  UserPreferences,
} from '@/src/modules/preferences/preferences'

const NUMBER_LOCALES: Record<NumberFormat, string> = {
  'en-US': 'en-US', // 1,234,567.89
  'en-IN': 'en-IN', // 12,34,567.89 (Indian numbering)
  'de-DE': 'de-DE', // 1.234.567,89
}

export function formatDate(
  input: Date | string | number | null | undefined,
  format: DateFormat,
  timezone?: string,
): string {
  if (input == null) return ''
  const d = input instanceof Date ? input : new Date(input)
  if (Number.isNaN(d.getTime())) return ''

  const opts: Intl.DateTimeFormatOptions = { timeZone: timezone || undefined }
  switch (format) {
    case 'DD/MM/YYYY':
      opts.day = '2-digit'
      opts.month = '2-digit'
      opts.year = 'numeric'
      return new Intl.DateTimeFormat('en-GB', opts).format(d)
    case 'MM/DD/YYYY':
      opts.day = '2-digit'
      opts.month = '2-digit'
      opts.year = 'numeric'
      return new Intl.DateTimeFormat('en-US', opts).format(d)
    case 'YYYY-MM-DD': {
      // ISO-like, but respect the user's timezone — Intl emits parts we can
      // safely reassemble.
      const parts = new Intl.DateTimeFormat('en-CA', {
        ...opts,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(d)
      const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
      return `${get('year')}-${get('month')}-${get('day')}`
    }
    case 'DD MMM YYYY':
    default:
      opts.day = '2-digit'
      opts.month = 'short'
      opts.year = 'numeric'
      return new Intl.DateTimeFormat('en-GB', opts).format(d)
  }
}

export function formatNumber(value: number, format: NumberFormat): string {
  if (!Number.isFinite(value)) return ''
  return new Intl.NumberFormat(NUMBER_LOCALES[format]).format(value)
}

/** Convenience: format a date+time together. */
export function formatDateTime(
  input: Date | string | number | null | undefined,
  prefs: Pick<UserPreferences, 'dateFormat' | 'timezone'>,
): string {
  if (input == null) return ''
  const date = formatDate(input, prefs.dateFormat, prefs.timezone)
  const time = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: prefs.timezone || undefined,
  }).format(new Date(input))
  return `${date} · ${time}`
}

/** Hook flavour — binds formatters to live preferences. */
export function useFormat() {
  const { preferences } = usePreferences()
  return useMemo(
    () => ({
      date: (input: Date | string | number | null | undefined) =>
        formatDate(input, preferences.dateFormat, preferences.timezone),
      number: (value: number) => formatNumber(value, preferences.numberFormat),
      dateTime: (input: Date | string | number | null | undefined) =>
        formatDateTime(input, preferences),
    }),
    [preferences],
  )
}
